import { AxiosError } from "axios";
import {
  Accordion,
  Button,
  Checkbox,
  Container,
  Flex,
  Form,
  Link,
  List,
  LoadingIcon,
  Stack,
  Text,
  TimeSpan,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import {
  getApplicationPayload,
  parseSubmitApplicationResponse,
  ParseSubmitApplicationResponseReturnType,
} from "components/IncreaseCreditLimit/utils";
import { Otp } from "components/Otp/Otp";
import { Separator } from "components/Separator/Separator";
import { postHref } from "library/api";
import { AccountInfo } from "library/hooks/types";
import { useLayout } from "library/hooks/useLayout";
import { formatAmount } from "library/moneyUtils";
import { useState } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { CreditCard } from "../CreditCard/CreditCard";
import { ResponsiveRow } from "../ResponsiveRow/ResponsiveRow";
import { Cancel } from "./Cancel";
import { CancelLink } from "./components/CancelLink";
import {
  getEmploymentStatusLabel,
  getOccupationLabel,
  getRelationshipStatusLabel,
  getResidentialStatusLabel,
  getYesNoLabel,
} from "./options";
import {
  ActionType,
  ApplicationStatus,
  ScreenProps,
  SubmitApplicationRequest,
  SubmitApplicationResponse,
} from "./types";

type OtpState = {
  createSessionUrl: string;
  token: string;
};

export const ReviewDetails = ({ state, dispatch }: ScreenProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [otpState, setOtpState] = useState<OtpState | null>(null);
  const navigate = useNavigate();

  const accountInfo = state.accountInfo as AccountInfo;
  const submitUrl = state.submitUrl as string;
  const initialValues = {
    iAgree: false,
  };
  const { scrollToTop } = useLayout();
  const submitMutation = useMutation<
    ParseSubmitApplicationResponseReturnType,
    AxiosError,
    { iAgree: boolean }
  >(
    async (values) => {
      const response = await postHref<
        SubmitApplicationResponse,
        SubmitApplicationRequest
      >(submitUrl, getApplicationPayload(state, values));
      const responseData = parseSubmitApplicationResponse(response.data);
      if (
        responseData === null ||
        responseData.application.status ===
          ApplicationStatus.INTERNAL_SERVICE_ERROR
      ) {
        throw new window.Error(
          "Invalid submit CLI application response received",
        );
      }
      return responseData;
    },
    {
      onSuccess: (responseData) => {
        if (responseData) {
          const { status } = responseData.application;
          switch (status) {
            case ApplicationStatus.CLI_FINAL_DECLINE: {
              dispatch({
                type: ActionType.FINAL_DECLINE,
              });
              return;
            }

            case ApplicationStatus.CLI_PENDING_ASSESSMENT:
            case ApplicationStatus.CLI_PENDING_CUST_CALL: {
              dispatch({
                type: ActionType.REVIEW_DETAILS_CLI_PENDING_CUST_CALL,
              });
              return;
            }

            case ApplicationStatus.CLI_PENDING_POI: {
              dispatch({
                type: ActionType.REVIEW_DETAILS_PENDING_POI,
                bankConnectUrl: responseData.bankConnectUrl,
                latitudePoiUrl: responseData.latitudePoiUrl as string,
              });
              return;
            }

            default: {
              dispatch({
                type: ActionType.READY_TO_CONFIRM_MMP,
                requestedCreditLimit:
                  responseData.application.requested_credit_limit,
                approvedCreditLimit:
                  responseData.application.approved_credit_limit,
                acknowledgementUrl: responseData.acknowledgementUrl,
                bankConnectUrl: responseData.bankConnectUrl,
                expiryDate: responseData.application.expiry_date,
                cancelUrl: responseData.cancelUrl,
                latitudePoiUrl: responseData.latitudePoiUrl as string,
                isConditionallyApproved: status === "CLI_CA_PENDING_ACCEPTANCE",
              });
            }
          }
        }
      },
      onError: (error) => {
        if (error.response?.data.type === "2FA_REQUIRED") {
          const createSessionUrl = error.response?.data.links.find(
            (link) => link.rel === "create_otp_session",
          )?.href;

          if (createSessionUrl) {
            setOtpState({
              token: state.accountInfo.cardToken,
              createSessionUrl,
            });
            return;
          }
        } else if (
          error.response?.data.title === ApplicationStatus.CLI_ACCOUNT_STATUS
        ) {
          dispatch({
            type: ActionType.CLI_NOT_ELIGIBLE,
            accountInfo,
          });
          return;
        }

        dispatch({
          type: ActionType.SUBMIT_APPLICATION_FAILED,
        });
      },
    },
  );

  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    submitMutation.mutate(values);

    scrollToTop();
  };

  if (otpState) {
    return (
      <Otp
        createSessionUrl={otpState.createSessionUrl}
        token={otpState.token}
        onSuccess={() => {
          submitMutation.mutate({ iAgree: true });
        }}
      />
    );
  }

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      <Stack gap="8" flatten>
        <Container bg="grey.t05" padding="4">
          <CreditCard
            cardName={accountInfo.cardName}
            cardEndsWith={accountInfo.cardEndsWith}
          />
        </Container>
        {!submitMutation.isLoading && (
          <Stack gap="4">
            <Text>
              <strong>Step 3 of 3</strong>
            </Text>
            <Text as="h3" textStyle="heading4">
              Review your details
            </Text>
          </Stack>
        )}
        {submitMutation.isLoading ? (
          <Flex direction="column" height="200" placeItems="center">
            <LoadingIcon size="medium" />
            <Text margin="10 0 0 0">
              We're working on it, this might take a few minutes.
            </Text>
            <Text>Please don't navigate away from this page.</Text>
          </Flex>
        ) : (
          <>
            <Separator />
            <Text>
              Confirm your details are correct and update if necessary.
            </Text>
            <Accordion
              color="secondary.lightBlue.t25"
              textColor="primary.blue.t100"
            >
              <Accordion.Item initiallyOpen>
                <Accordion.Item.Header>About you</Accordion.Item.Header>
                <Accordion.Item.Content>
                  <Stack gap="12">
                    <Stack gap="4">
                      <Text textStyle="subtitle2">
                        <strong>Personal information</strong>
                      </Text>
                      <ResponsiveRow ratio="3/5">
                        <Text>Relationship status</Text>
                        <Text testId="relationship-status">
                          <strong>
                            {getRelationshipStatusLabel(
                              state.personalInformation.relationshipStatus,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Number of dependants</Text>
                        <Text testId="number-of-dependants">
                          <strong>
                            {state.personalInformation.numberOfDependants}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                    </Stack>
                    <Stack gap="4">
                      <Text textStyle="subtitle2">
                        <strong>Residential address</strong>
                      </Text>
                      <ResponsiveRow ratio="3/5">
                        <Text>Residential status</Text>
                        <Text testId="residential-status">
                          <strong>
                            {getResidentialStatusLabel(
                              state.residentialAddress.residentialStatus,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Time at current address</Text>
                        <Text testId="time-at-current-address">
                          <strong>
                            {TimeSpan.getHelpText(
                              state.residentialAddress.timeAtCurrentAddress
                                .years,
                              state.residentialAddress.timeAtCurrentAddress
                                .months,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                    </Stack>
                    <Stack gap="4">
                      <Text textStyle="subtitle2">
                        <strong>Employment details</strong>
                      </Text>
                      <ResponsiveRow ratio="3/5">
                        <Text>Employment status</Text>
                        <Text testId="employment-status">
                          <strong>
                            {getEmploymentStatusLabel(
                              state.employmentDetails.employmentStatus,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Occupation</Text>
                        <Text testId="occupation">
                          <strong>
                            {getOccupationLabel(
                              state.employmentDetails.occupation,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Time with current employer</Text>
                        <Text testId="time-with-current-employer">
                          <strong>
                            {TimeSpan.getHelpText(
                              state.employmentDetails.timeWithCurrentEmployer
                                .years,
                              state.employmentDetails.timeWithCurrentEmployer
                                .months,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                    </Stack>
                    <Link
                      href="#"
                      newTab={false}
                      onClick={(e) => {
                        e.preventDefault();
                        dispatch({
                          type: ActionType.REVIEW_DETAILS_ABOUT_YOU_UPDATE_DETAILS_CLICKED,
                        });
                      }}
                    >
                      Update details
                    </Link>
                  </Stack>
                </Accordion.Item.Content>
              </Accordion.Item>
              <Accordion.Item initiallyOpen>
                <Accordion.Item.Header>
                  Your income and expenses
                </Accordion.Item.Header>
                <Accordion.Item.Content>
                  <Stack gap="12">
                    <Stack gap="4">
                      <Text textStyle="subtitle2">
                        <strong>Your income</strong>
                      </Text>
                      <ResponsiveRow ratio="3/5">
                        <Text>Net income</Text>
                        <Text testId="net-income">
                          <strong>
                            {formatAmount(state.income.netIncome.amount)}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Do you have any other sources of income?</Text>
                        <Text testId="have-other-source-of-income">
                          <strong>
                            {getYesNoLabel(state.income.otherSourcesOfIncome)}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      {state.income.otherSourcesOfIncome === "yes" && (
                        <ResponsiveRow ratio="3/5">
                          <Text>Other income</Text>
                          <Text testId="other-income">
                            <strong>
                              {formatAmount(state.income.otherIncome.amount)}
                            </strong>
                          </Text>
                        </ResponsiveRow>
                      )}
                    </Stack>
                    <Stack gap="4">
                      <Text textStyle="subtitle2">
                        <strong>Monthly expenses</strong>
                      </Text>
                      <ResponsiveRow ratio="3/5">
                        <Text>Do you share your living expenses?</Text>
                        <Text testId="share-living-expenses">
                          <strong>
                            {getYesNoLabel(
                              state.monthlyExpenses.shareLivingExpenses,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Mortgages or rent</Text>
                        <Text testId="mortgages-or-rent">
                          <strong>
                            {formatAmount(
                              state.residentialAddress.mortgageOrRent,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>
                          Eating out, groceries, lifestyle and entertainment
                        </Text>
                        <Text testId="eating-out">
                          <strong>
                            {formatAmount(
                              state.monthlyExpenses.expenses.lifestyle,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Education &amp; childcare</Text>
                        <Text testId="education">
                          <strong>
                            {formatAmount(
                              state.monthlyExpenses.expenses.education,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Transport</Text>
                        <Text testId="transport">
                          <strong>
                            {formatAmount(
                              state.monthlyExpenses.expenses.transport,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Insurances</Text>
                        <Text testId="insurances">
                          <strong>
                            {formatAmount(
                              state.monthlyExpenses.expenses.insurances,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Utilities</Text>
                        <Text testId="utilities">
                          <strong>
                            {formatAmount(
                              state.monthlyExpenses.expenses.utilities,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      <ResponsiveRow ratio="3/5">
                        <Text>Personal and other expenses</Text>
                        <Text testId="personal">
                          <strong>
                            {formatAmount(
                              state.monthlyExpenses.expenses.personal,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                    </Stack>
                    <Stack gap="4">
                      <Text textStyle="subtitle2">
                        <strong>Other credit cards and loans</strong>
                      </Text>
                      <ResponsiveRow ratio="3/5">
                        <Text>
                          Do you have any other credit cards? (not including
                          your {accountInfo.cardName})
                        </Text>
                        <Text testId="have-other-credit-cards">
                          <strong>
                            {getYesNoLabel(
                              state.additionalDebts.haveAdditionalDebts,
                            )}
                          </strong>
                        </Text>
                      </ResponsiveRow>
                      {state.additionalDebts.haveAdditionalDebts === "yes" && (
                        <>
                          <ResponsiveRow ratio="3/5">
                            <Text>Loan repayments</Text>
                            <Text testId="loan-repayments">
                              <strong>
                                {formatAmount(
                                  state.additionalDebts.loanRepayments,
                                )}
                              </strong>
                            </Text>
                          </ResponsiveRow>
                          <ResponsiveRow ratio="3/5">
                            <Text>Total credit card limit</Text>
                            <Text testId="total-credit-card-limit">
                              <strong>
                                {formatAmount(
                                  state.additionalDebts.totalCreditLimit,
                                )}
                              </strong>
                            </Text>
                          </ResponsiveRow>
                          <ResponsiveRow ratio="3/5">
                            <Text>Total amounts owing</Text>
                            <Text testId="total-amounts-owing">
                              <strong>
                                {formatAmount(state.additionalDebts.totalOwing)}
                              </strong>
                            </Text>
                          </ResponsiveRow>
                        </>
                      )}
                    </Stack>
                    <Link
                      href="#"
                      newTab={false}
                      onClick={(e) => {
                        e.preventDefault();
                        dispatch({
                          type: ActionType.REVIEW_DETAILS_INCOME_AND_EXPENSES_UPDATE_DETAILS_CLICKED,
                        });
                      }}
                    >
                      Update details
                    </Link>
                  </Stack>
                </Accordion.Item.Content>
              </Accordion.Item>
            </Accordion>
            <Stack gap="2">
              <Text>I confirm that:</Text>
              <List>
                <List.Item>
                  My credit limit be increased to{" "}
                  {formatAmount(state.newCreditLimit)}, or such lesser credit
                  limit that Latitude Financial Services is willing to provide
                  me.
                </List.Item>
                <List.Item>
                  Electronic income verification will be attempted by our
                  service providers, including Verifier (see{" "}
                  <Link
                    href="#"
                    newTab={false}
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch({
                        type: ActionType.REVIEW_DETAILS_VERIFIER_TERMS_CLICKED,
                      });
                    }}
                  >
                    Verifier's terms of service
                  </Link>
                  ). If your income can't be verified via these means, you'll be
                  provided an alternative option.
                </List.Item>
                <List.Item>
                  Latitude Financial Services will complete a credit check
                  during my application to increase my credit limit.
                </List.Item>
                <List.Item>
                  The information I have provided in this application is correct
                  and I have not withheld any information which could cause
                  Latitude Financial Services to incorrectly grant credit to me.
                </List.Item>
                <List.Item>
                  I have read and agree to the{" "}
                  <Link
                    href="https://www.latitudefinancial.com.au/privacy"
                    newTab
                  >
                    Important Privacy Notice
                  </Link>
                  .
                </List.Item>
                <List.Item>
                  I understand that regardless of the outcome of my application,
                  or if I choose to cancel after submitting, I will need to wait
                  at least 30 days before applying again.
                </List.Item>
              </List>
            </Stack>
            <Checkbox name="iAgree" label="Agree to all the above" hideLabel>
              I have read and agree to all of the above
            </Checkbox>
            <Container width="100%" width-sm="400">
              <FormActions>
                <FormActions.Primary>
                  <Button type="submit">Next</Button>
                </FormActions.Primary>
                <FormActions.Secondary>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      dispatch({
                        type: ActionType.REVIEW_DETAILS_BACK_CLICKED,
                      });
                    }}
                  >
                    Back
                  </Button>
                </FormActions.Secondary>
              </FormActions>
            </Container>
            <CancelLink onClick={() => setIsModalOpen(true)} />
          </>
        )}
      </Stack>
      <Cancel
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => {
          navigate("/manage-account");
        }}
      />
    </Form>
  );
};
